<template>
  <Head title="Portex Pledges" />
  <MainLayout>
    <div class="h-screen overflow-y-auto scrollbar-hide">
      <div class="flex flex-col items-center justify-start text-center">
        <section class="flex flex-col items-center pt-16 md:pt-24 2xl:pt-32 px-6 md:px-8 lg:px-16 w-full h-[calc(100vh-64px)] relative ">
          <h1
            class="text-2xl md:text-3xl  font-bold w-fit mb-16"
            style="background: linear-gradient(to right, #9EFFF4, #62DA92); -webkit-background-clip: text; background-clip: text; color: transparent;"
          >
            Accountability. Reimagined.
          </h1>


          <div class="text-3xl md:text-4xl 2xl:text-5xl font-bold  text-center  w-full h-56 md:h-32 mb-10">
            <span
              class="font-bold bg-gradient-to-r from-gray-400 to-gray-200 bg-clip-text text-transparent"
            >I
              pledge&nbsp;</span>
            <span class="inline break-words">
              <span :class="currentPledgeIndex === 0 ? 'text-[#93c5fd]' : 'text-[#22d3ee]'">{{ displayText
              }}</span>
              <span class="inline-block w-0.5 h-[1em] bg-white animate-cursor" />
            </span>
          </div>


          <div class="text-gray-400 max-w-2xl px-4 md:px-0 text-base 2xl:text-lg mb-8 2xl:mb-16">
            Pledges are applications that help you <span class="text-white font-bold">commit to your
              goals</span>
            while <span class="text-white font-bold">taking ownership of your data</span> along the way.
          </div>


          <a 
            href="/gpt"
            class="bg-gradient-to-r from-[#22D3EE] to-[#6DE0A2] hover-effect text-slate-900 px-4 md:px-6 py-3 mb-16 rounded-3xl flex items-center gap-5"
          >
            <div class="flex flex-col justify-center items-start text-base 2xl:text-xl font-bold ">
              Make a Pledge
              <div class="text-xs 2xl:text-base opacity-75">It only takes a few minutes.</div>
            </div>
            <div class="h-7 w-7 2xl:h-8 2xl:w-8">
              <GradientArrowCircle 
                class="text-slate-900"
              />
            </div>
          </a>

          <div
            class="absolute bottom-0 md:bottom-6  animate-bounce cursor-pointer"
            @click="scrollToNextSection"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="32" 
              height="32" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              stroke-linecap="round" 
              stroke-linejoin="round" 
              class="text-white hover:text-[#22D3EE] transition-colors duration-500"
            >
              <path d="M7 13l5 5 5-5" />
              <path d="M7 6l5 5 5-5" />
            </svg>
          </div>
        </section>


        <section class="py-20 w-full bg-slate-900">
          <div class="flex px-6 md:px-8 lg:px-16 items-center justify-center">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-56 mb-12 max-w-7xl w-full justify-between">
              <div class="text-3xl md:text-4xl 2xl:text-5xl font-bold text-white text-start">
                Crush your goals.<br>
                Own your data.
              </div>


              <div class="flex flex-col space-y-4">
                <p class="text-gray-400 text-start text-sm md:text-base 2xl:text-lg">
                  Pledges combine the data from your favorite applications with the power of smart
                  contracts to create real and meaningful accountability.
                </p>
                <a
                  href="/register"
                  class="text-blue-500 hover:text-blue-400 flex items-center gap-2 text-sm md:text-base 2xl:text-lg"
                >
                  See all available Pledge apps
                  <span class="text-lg md:text-xl 2xl:text-2xl">→</span>
                </a>
              </div>
            </div>
          </div>
          <div class="divider divider-neutral mx-8" />
        </section>


        <section class=" w-full bg-slate-900">
          <div class="flex px-6 md:px-8 lg:px-16 items-center justify-center">
            <div class="grid grid-cols-1 md:grid-cols-2 items-center justify-between gap-10 lg:gap-56 mb-12 max-w-7xl w-full">
              <div class="flex flex-col space-y-4">
                <h2 class="text-3xl md:text-4xl 2xl:text-5xl font-bold text-white text-start">
                  Start owning your<br>
                  data with Vaults
                </h2>
                <p class="text-gray-400 text-start max-w-xl text-sm md:text-base 2xl:text-lg">
                  You control every data point that is fetched when using Pledges,
                  and have the ability to control how that data is used or monetized.
                </p>
                <a
                  href="https://docs.portexai.com/portex-docs/core-concepts/vault"
                  target="_blank"
                  class="text-blue-500 hover:text-blue-400 flex items-center gap-2 text-sm md:text-base 2xl:text-lg"
                >
                  Read more about Vaults
                  <span class="text-lg md:text-xl 2xl:text-2xl">→</span>
                </a>
              </div>


              <div class="flex flex-col space-y-4 max-w-sm">
                <div class="flex items-center justify-between">
                  <div class="flex flex-col gap-2 flex-1 mr-2">
                    <div class="bg-[#D1D5DB] h-2 w-[50%] rounded" />
                    <div class="bg-[#E5E7EB] h-2 w-[30%] rounded" />
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="white"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8 10V7a4 4 0 1 1 8 0v3h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h1Zm2-3a2 2 0 1 1 4 0v3h-4V7Zm2 6a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1Z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <div class="bg-white h-0.5" />
                <div class="flex items-center justify-between">
                  <div class="flex flex-col gap-2 flex-1 mr-2">
                    <div class="bg-[#D1D5DB] h-2 w-[50%] rounded" />
                    <div class="bg-[#E5E7EB] h-2 w-[30%] rounded" />
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="white"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8 10V7a4 4 0 1 1 8 0v3h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h1Zm2-3a2 2 0 1 1 4 0v3h-4V7Zm2 6a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1Z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <div class="bg-white h-0.5" />
                <div class="flex items-center justify-between">
                  <div class="flex flex-col gap-2 flex-1 mr-2">
                    <div class="bg-[#D1D5DB] h-2 w-[50%] rounded" />
                    <div class="bg-[#E5E7EB] h-2 w-[30%] rounded" />
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="white"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8 10V7a4 4 0 1 1 8 0v3h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h1Zm2-3a2 2 0 1 1 4 0v3h-4V7Zm2 6a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1Z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <div class="bg-white h-0.5" />
                <div class="flex items-center justify-between">
                  <div class="flex flex-col gap-2 flex-1 mr-2">
                    <div class="bg-[#D1D5DB] h-2 w-[50%] rounded" />
                    <div class="bg-[#E5E7EB] h-2 w-[30%] rounded" />
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="white"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8 10V7a4 4 0 1 1 8 0v3h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h1Zm2-3a2 2 0 1 1 4 0v3h-4V7Zm2 6a1 1 0 0 1 1 1v3a1 1 0 1 1-2 0v-3a1 1 0 0 1 1-1Z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div class="divider divider-neutral mx-8" />
        </section>

        <section class="w-full bg-slate-900">
          <div class="flex px-6 md:px-8 lg:px-16 items-center justify-center">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-56 mb-12 max-w-7xl w-full items-center">
              <div class="flex flex-col w-fit p-4">
                <RoundCounter />
              </div>


              <div class="flex flex-col space-y-4 py-16">
                <h2 class="text-3xl md:text-4xl 2xl:text-5xl font-bold text-white flex items-center gap-5">
                  Join this Round
                  <a
                    href="/register"
                    class="bg-gradient-to-r from-[#22D3EE] to-[#6DE0A2] hover-effect rounded-full p-1.5  h-8 w-8 2xl:p-2 2xl:h-10 2xl:w-10 flex items-center justify-center transition-all duration-300 cursor-pointer"
                  >
                    <GradientArrowCircle class=" h-5 w-5 2xl:h-6 2xl:w-6 text-slate-900" />
                  </a>
                </h2>
                <p class="text-gray-400 max-w-lg text-start text-sm md:text-base 2xl:text-lg">
                  Pledges run each week in Rounds, and are evaluated every Sunday at 11:59 PM ET.
                </p>
                <a
                  href=" https://docs.portexai.com/portex-docs/pledges/pledges-overview"
                  target="_blank"
                  class="text-blue-500 hover:text-blue-400 flex items-center gap-2 text-sm md:text-base 2xl:text-lg"
                >
                  Learn how Pledges are evaluated
                  <span class="text-lg md:text-xl 2xl:text-2xl">→</span>
                </a>
              </div>
            </div>
          </div>
          <div class="divider divider-neutral mx-8" />
        </section>
        <section class="w-full bg-slate-900 py-16">
          <div class="flex flex-col max-w-7xl px-6 md:px-8 lg:px-16 2xl:px-0 mx-auto">
            <h2 class="text-3xl md:text-4xl 2xl:text-5xl font-bold text-white mb-12 text-start">
              Featured Pledges
            </h2>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 2xl:gap-8 mb-16 text-start ">
              <div
                v-for="card in featuredCards" 
                :key="card.title"
              >
                <CardDapp :card="card" />
              </div>
            </div>

            <div class="relative">
              <div class="absolute inset-0 rounded-xl bg-gradient-to-r from-[#7EF4CB] to-[#1F98DF] p-[1px]">
                <div class="w-full h-full bg-slate-900 rounded-xl" />
              </div>

              <div class="relative flex items-center px-6 py-3">
                <div class="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-12 text-sm 2xl:text-base text-gray-400 w-full">
                  <span class="text-[#7EF4CB] font-bold text-2xl 2xl:text-3xl">Learn</span>
                  <div class="flex-1 flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-4 md:justify-around w-full">
                    <a
                      href="https://docs.portexai.com/portex-docs/pledges/pledges-faq#what-is-a-pledge" 
                      target="_blank" 
                      class="hover:text-white relative group cursor-pointer whitespace-nowrap"
                    >
                      What are Pledges?
                      <span class="absolute bottom-0 left-0 w-0 h-[1px] bg-gradient-to-r from-[#7EF4CB] to-[#1F98DF] group-hover:w-full transition-all duration-300" />
                    </a>
                    <a
                      href="https://docs.portexai.com/portex-docs/pledges/pledges-faq#how-do-i-create-a-pledge" 
                      target="_blank" 
                      class="hover:text-white relative group cursor-pointer whitespace-nowrap"
                    >
                      How to get started
                      <span class="absolute bottom-0 left-0 w-0 h-[1px] bg-gradient-to-r from-[#7EF4CB] to-[#1F98DF] group-hover:w-full transition-all duration-300" />
                    </a>
                    <a
                      href="https://docs.portexai.com/portex-docs/pledges/pledges-faq#how-is-my-data-used-in-a-pledge" 
                      target="_blank" 
                      class="hover:text-white relative group cursor-pointer whitespace-nowrap"
                    >
                      How your data is used
                      <span class="absolute bottom-0 left-0 w-0 h-[1px] bg-gradient-to-r from-[#7EF4CB] to-[#1F98DF] group-hover:w-full transition-all duration-300" />
                    </a>
                    <a
                      href="https://zmjn4wcgn1e.typeform.com/to/bCYmcTD6?typeform-source=portex-dev" 
                      target="_blank" 
                      class="hover:text-white relative group cursor-pointer whitespace-nowrap"
                    >
                      Get in touch
                      <span class="absolute bottom-0 left-0 w-0 h-[1px] bg-gradient-to-r from-[#7EF4CB] to-[#1F98DF] group-hover:w-full transition-all duration-300" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section class="h-[60vh] w-full bg-slate-900 flex items-center justify-center font-['Work_Sans']">
          <div class="max-w-6xl mx-auto px-6 md:px-8 lg:px-16 text-center">
            <div class="flex flex-col items-center justify-center space-y-4 2xl:space-y-8">
              <div class="text-white text-xl uppercase tracking-[0.25em] font-medium">
                POWERED BY
              </div>

              <div class="flex items-center justify-center w-full max-w-md">
                <div>
                  <PortexLogo class="hover:opacity-80 transition-opacity duration-300" />
                </div>
              </div>

              <div class="flex flex-col items-center space-y-2 2xl:space-y-4 text-gray-400 text-base">
                <div>© Portex AI Inc. - All rights reserved.</div>
                <div class="flex items-center gap-2">
                  Follow us on 
                  <a
                    href="https://x.com/0xpledges"
                    target="_blank"
                    class="group"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16" 
                      class="transition-colors duration-200 fill-[#20AAFA] hover-effect" 
                      viewBox="0 0 16 16"
                    >
                      <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865l8.875 11.633Z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </MainLayout>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import MainLayout from '@/Layouts/MainLayout.vue';
import { Head } from '@inertiajs/vue3';
import GradientArrowCircle from '@/Icons/GradientArrowCircle.vue';
import RoundCounter from '@/Components/Header/RoundCounter.vue';
import PortexLogo from '@/Components/Header/PortexLogo.vue';
import CardDapp from '@/Pages/Gpt/Components/CardDapp.vue';

const props = defineProps({
    round: Object
});

const pledges = ref([
    'to run 5 miles by Sunday or donate $150 to St. Jude.',
    'to finish this course by Sunday or donate $200 to Doctors Without Borders.'
]);

const currentPledgeIndex = ref(0);
const displayText = ref('');
const typingInterval = null;

const currentRound = computed(() => {
    return props.round.current_round;
});

const typePledge = async () => {
    const text = pledges.value[currentPledgeIndex.value];
    displayText.value = '';
    
    for (let i = 0; i < text.length; i++) {
        displayText.value += text[i];
        await new Promise(resolve => setTimeout(resolve, 60));
    }
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    while (displayText.value.length > 0) {
        displayText.value = displayText.value.slice(0, -1);
        await new Promise(resolve => setTimeout(resolve, 30));
    }
    
    await new Promise(resolve => setTimeout(resolve, 500));
    currentPledgeIndex.value = (currentPledgeIndex.value + 1) % pledges.value.length;
};

const startTyping = async () => {
    while (true) {
        await typePledge();
    }
};

onMounted(() => {
    startTyping();
});

onUnmounted(() => {
    displayText.value = '';
});

const featuredCards = ref([
    {
        title: 'Runner',
        app: 'Strava',
        logo: '/images/dapp_runner.png',
        app_icon: '/images/strava_logo.webp',
        description: 'I want to run 3 miles by Sunday, if I fail, donate $100 to charity',
        active: true,
        category_icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m7.171 12.906-2.153 6.411 2.672-.89 1.568 2.34 1.825-5.183m5.73-2.678 2.154 6.411-2.673-.89-1.568 2.34-1.825-5.183M9.165 4.3c.58.068 1.153-.17 1.515-.628a1.681 1.681 0 0 1 2.64 0 1.68 1.68 0 0 0 1.515.628 1.681 1.681 0 0 1 1.866 1.866 1.68 1.68 0 0 0 .628 1.515 1.681 1.681 0 0 1 0 2.64 1.682 1.682 0 0 0-.628 1.515 1.681 1.681 0 0 1-1.866 1.866 1.681 1.681 0 0 0-1.516.628 1.681 1.681 0 0 1-2.639 0 1.682 1.682 0 0 0-1.515-.628 1.681 1.681 0 0 1-1.867-1.866 1.681 1.681 0 0 0-.627-1.516 1.681 1.681 0 0 1 0-2.639c.458-.361.696-.935.627-1.515A1.681 1.681 0 0 1 9.165 4.3ZM14 9a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z"/></svg>',
        category_color: '#22D3EE',
        trending: true
    },
    {
        title: 'Speaker',
        app: 'Duolingo',
        logo: '/images/speaker.png',
        app_icon: '/images/duolingo_logo.png',
        description: 'I want to keep a 3-week streak of learning Italian, if I fail, donate $250',
        active: true,
        category_icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M4.37 7.657c2.063.528 2.396 2.806 3.202 3.87 1.07 1.413 2.075 1.228 3.192 2.644 1.805 2.289 1.312 5.705 1.312 6.705M20 15h-1a4 4 0 0 0-4 4v1M8.587 3.992c0 .822.112 1.886 1.515 2.58 1.402.693 2.918.351 2.918 2.334 0 .276 0 2.008 1.972 2.008 2.026.031 2.026-1.678 2.026-2.008 0-.65.527-.9 1.177-.9H20M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>',
        category_color: '#DCDD8E',
        trending: true
    },
    {
        title: 'Learner',
        app: 'Coursera',
        logo: '/images/learner.png',
        app_icon: '/images/coursera_logo.png',
        description: 'I want to finish a course on Rust by year end, if I fail, donate $100 to charity',
        active: true,
        category_icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18.5A2.493 2.493 0 0 1 7.51 20H7.5a2.468 2.468 0 0 1-2.4-3.154 2.98 2.98 0 0 1-.85-5.274 2.468 2.468 0 0 1 .92-3.182 2.477 2.477 0 0 1 1.876-3.344 2.5 2.5 0 0 1 3.41-1.856A2.5 2.5 0 0 1 12 5.5m0 13v-13m0 13a2.493 2.493 0 0 0 4.49 1.5h.01a2.468 2.468 0 0 0 2.403-3.154 2.98 2.98 0 0 0 .847-5.274 2.468 2.468 0 0 0-.921-3.182 2.477 2.477 0 0 0-1.875-3.344A2.5 2.5 0 0 0 14.5 3 2.5 2.5 0 0 0 12 5.5m-8 5a2.5 2.5 0 0 1 3.48-2.3m-.28 8.551a3 3 0 0 1-2.953-5.185M20 10.5a2.5 2.5 0 0 0-3.481-2.3m.28 8.551a3 3 0 0 0 2.954-5.185"/></svg>',
        category_color: '#6DE0A2',
        trending: true
    }
]);

const scrollToNextSection = () => {
    const nextSection = document.querySelector('section:nth-of-type(2)');
    if (nextSection) {
        nextSection.scrollIntoView({ behavior: 'smooth' });
    }
};
</script>
<style>
@keyframes cursor {
    to {
        visibility: hidden;
    }
}

.animate-cursor {
    animation: cursor 1s steps(2, start) infinite;
}

.animate-bounce {
    animation: bounce 1s infinite;
}

@keyframes bounce {

    0%,
    100% {
        transform: translateY(-25%);
        animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
    }

    50% {
        transform: translateY(0);
        animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
    }
}

.scrollbar-hide {
    -ms-overflow-style: none;
    /* IE and Edge */
    scrollbar-width: none;
    /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
    display: none;
    /* Chrome, Safari and Opera */
}

.hover-effect {
    transition: all 0.3s ease;
}

.hover-effect:hover {
    filter: brightness(1.1);
}
</style>
